import { type ReactNode, useEffect, useState } from 'react';
import type { SpaConfig, SpaEnvironmentConfig } from './config';

interface EnvironmentHealth {
  status?: string;
  stage?: string;
}

export type EnvironmentCheck =
  | { status: 'checking' }
  | { status: 'ready' }
  | { status: 'blocked'; message: string };

export function environmentCode(stage: string): string {
  return stage.trim().toUpperCase() || 'UNKNOWN';
}

export function environmentName(stage: string): string {
  switch (stage.trim().toLowerCase()) {
    case 'dev':
      return 'Development';
    case 'prod':
      return 'Production';
    case 'sandbox':
      return 'Sandbox';
    case 'local':
      return 'Local';
    default:
      return 'Unknown';
  }
}

export function environmentDocumentTitle(stage: string): string {
  const normalized = stage.trim().toLowerCase();
  return normalized === 'prod' ? 'TCVS Vault' : `[${environmentCode(stage)}] TCVS Vault`;
}

export function isDevelopmentEnvironment(environment: SpaEnvironmentConfig): boolean {
  return environment.stage.trim().toLowerCase() === 'dev';
}

export function evaluateEnvironmentHealth(
  expectedStage: string,
  health: EnvironmentHealth,
): EnvironmentCheck {
  const expected = expectedStage.trim().toLowerCase();
  const actual = health.stage?.trim().toLowerCase() ?? '';
  if (health.status !== 'ok' || actual === '') {
    return {
      status: 'blocked',
      message: 'The API health response did not identify a healthy environment.',
    };
  }
  if (expected === '' || expected === 'unknown' || expected !== actual) {
    return {
      status: 'blocked',
      message: `This frontend expects ${environmentCode(expectedStage)}, but the API reports ${environmentCode(actual)}.`,
    };
  }
  return { status: 'ready' };
}

export function EnvironmentGuard({
  config,
  buildRevision,
  children,
}: {
  config: SpaConfig;
  buildRevision: string;
  children: ReactNode;
}): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const [check, setCheck] = useState<EnvironmentCheck>(
    config.environment.stage === 'local' ? { status: 'ready' } : { status: 'checking' },
  );

  useEffect(() => {
    document.title = environmentDocumentTitle(config.environment.stage);
  }, [config.environment.stage]);

  useEffect(() => {
    if (config.environment.stage === 'local') {
      setCheck({ status: 'ready' });
      return;
    }
    const controller = new AbortController();
    setCheck({ status: 'checking' });
    void fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/health`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`API health returned HTTP ${response.status}`);
        }
        return (await response.json()) as EnvironmentHealth;
      })
      .then((health) => {
        setCheck(evaluateEnvironmentHealth(config.environment.stage, health));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setCheck({
          status: 'blocked',
          message:
            error instanceof Error
              ? `The environment safety check could not reach the API: ${error.message}`
              : 'The environment safety check could not reach the API.',
        });
      });
    return () => controller.abort();
  }, [attempt, config.apiBaseUrl, config.environment.stage]);

  if (check.status === 'ready') return <>{children}</>;

  return (
    <main className="tcvs-environment-check-main">
      <EnvironmentBanner environment={config.environment} />
      <section className="tcvs-environment-check-panel" aria-live="polite">
        <span className="tcvs-brand">
          <span className="tcvs-brand-mark" aria-hidden="true" />
          TCVS Vault
          <EnvironmentBadge environment={config.environment} />
        </span>
        <h1>{check.status === 'checking' ? 'Checking environment' : 'Environment blocked'}</h1>
        <p>
          {check.status === 'checking'
            ? `Verifying ${environmentName(config.environment.stage)} before sign-in.`
            : check.message}
        </p>
        {check.status === 'blocked' ? (
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Retry
          </button>
        ) : null}
        <EnvironmentDetails environment={config.environment} buildRevision={buildRevision} />
      </section>
    </main>
  );
}

export function EnvironmentBanner({
  environment,
}: {
  environment: SpaEnvironmentConfig;
}): JSX.Element | null {
  if (!isDevelopmentEnvironment(environment)) return null;
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  return (
    <div className="tcvs-environment-banner" role="status">
      <strong>DEV ENVIRONMENT</strong>
      <span aria-hidden="true">|</span>
      <span>TEST DATA</span>
      {hostname === '' ? null : (
        <>
          <span aria-hidden="true">|</span>
          <span>{hostname}</span>
        </>
      )}
    </div>
  );
}

export function EnvironmentBadge({
  environment,
}: {
  environment: SpaEnvironmentConfig;
}): JSX.Element | null {
  if (!isDevelopmentEnvironment(environment)) return null;
  return <span className="tcvs-environment-badge">DEV</span>;
}

export function EnvironmentDetails({
  environment,
  buildRevision,
}: {
  environment: SpaEnvironmentConfig;
  buildRevision: string;
}): JSX.Element {
  const code = environmentCode(environment.stage);
  return (
    <details className="tcvs-environment-details">
      <summary>{code}</summary>
      <dl>
        <div>
          <dt>Environment</dt>
          <dd>{environmentName(environment.stage)}</dd>
        </div>
        <div>
          <dt>AWS account</dt>
          <dd>{environment.account}</dd>
        </div>
        <div>
          <dt>Region</dt>
          <dd>{environment.region}</dd>
        </div>
        <div>
          <dt>Build</dt>
          <dd>{buildRevision}</dd>
        </div>
      </dl>
    </details>
  );
}
